/**
 * SortingDrawer UI Component
 *
 * This class manages the sorting drawer for the search results page, allowing users to select sorting options
 * for homes or communities. It handles:
 * - Rendering the appropriate sorting options based on the current product type.
 * - Managing the drawer's lifecycle and event binding/unbinding.
 * - Dispatching sort actions to the Redux store when the user selects a new sort option.
 * - Integrating with the drawer service to display and clear the drawer UI.
 *
 * The SortingDrawer ensures a consistent and interactive sorting experience for users
 * across different product types in the search results interface.
 */

import { store } from "@redux/store";
import { ProductType, searchParametersActions } from "features/searchResultsFacets";
import drawerService from "layout/services/drawerService";

export default class SortingDrawer {
  constructor() {
    this.id = 'sorting-drawer';
    this.productType = null;
    this.currentSortBy = null;
    this.currentSortOrder = null;
    this.onSortChangeEvent = null;
  }

 /**
   * Sets the metadata for the SortingDrawer instance.
   *
   * - Updates the productType property based on the provided metadata object.
   * - This determines which set of sorting options will be rendered in the drawer.
   *
   * @param {Object} metadata - An object containing configuration for the drawer.
  */
  setMetadata(metadata) {
    this.productType = metadata?.productType;
    this.currentSortBy = metadata?.sorting?.sortBy;
    this.currentSortOrder = metadata?.sorting?.sortOrder;
  }

 /**
   * Handles the sort option change event in the sorting drawer.
   *
   * - Extracts the selected sort option and order from the clicked input element.
   * - If the selected sort option and order are unchanged, does nothing.
   * - Otherwise, updates the currentSortBy and currentSortOrder properties.
   * - Dispatches a fetchSort action to the Redux store with the new sort parameters.
   * - Clears the drawer UI after dispatching the sort change.
   *
   * @param {Event} event - The click event triggered on the sorting drawer.
  */
  dispatchSortChangeEvent(event) {
    const selectedOption = event.target.querySelector("input");
    if (!selectedOption) {
      return;
    }

    const sortBy = selectedOption.value.split(',')[0];
    const sortOrder = selectedOption.value.split(',')[1] === 'true';

    if (this.currentSortBy === sortBy && this.currentSortOrder === sortOrder) {
      drawerService.clearHtml();
      return;
    }

    this.currentSortBy = sortBy;
    this.currentSortOrder = sortOrder;

    store.dispatch(searchParametersActions.fetchSort({
      sortBy,
      sortOrder,
    }));

    drawerService.clearHtml();
  }

/**
  * Binds event listeners to the sorting drawer UI.
  *
  * - Retrieves the drawer element by its ID.
  * - Binds the click event to the drawer, using the dispatchSortChangeEvent handler.
  * - Stores the bound handler reference for later removal.
  *
  * This ensures that user interactions with the sorting options are handled and dispatched correctly.
  */
  bindEvents() {
    const drawer = this.getDrawer();
    this.onSortChangeEvent = this.dispatchSortChangeEvent.bind(this);
    drawer.addEventListener('click', this.onSortChangeEvent);
  }

/**
  * Unbinds event listeners from the sorting drawer UI.
  *
  * - Retrieves the drawer element by its ID.
  * - Removes the click event listener that was previously bound using bindEvents.
  * - Ensures that no duplicate or stale event handlers remain attached when the drawer is removed or re-initialized.
  *
  * This helps prevent memory leaks and unintended behavior from lingering event listeners.
  */
  unbindEvents() {
    const drawer = this.getDrawer();
    drawer.removeEventListener('click', this.onSortChangeEvent);
  }

/**
  * Retrieves the DOM element representing the sorting drawer by its unique ID.
  *
  * @returns {HTMLElement|null} The drawer element if found, otherwise null.
  */
  getDrawer() {
    return document.getElementById(this.id);
  }

  /**
   * Helper to generate a radio option label.
   * @param {string} label
   * @param {string} value
   * @returns {string}
   */
  getRadioOption(label, value) {
    const isActiveSorting = (!value && !this.currentSortBy) || value === `${this.currentSortBy},${this.currentSortOrder}`;
    const labelFontColor = isActiveSorting ? 'nhs-u-color--teal-700' : 'nhs-u-color--black-1000';

    const sortOption = `
      <label class="nhs-u-items-flex-start nhs-u-padding--y-100 nhs-u-padding--x-500 nhs-o-typography--sans-body-1-medium-book ${labelFontColor}">
        <input type="radio" value="${value}" hidden>
        ${label}
      </label>
    `;

    if (isActiveSorting) {
      return `
        <div class="nhs-drawer__option--selected">
          ${sortOption}
        </div>
      `;
    }

    return sortOption;
  }

  /**
   * Generates and returns the HTML string for the sorting drawer for a given options array.
   * @param {Array<{label: string, value: string}>} options
   * @returns {string}
   */
  getHtmlForOptions(options) {
    return `
      <div id='${this.id}' class="nhs-u-width--full">
        <legend class="nhs-u-padding--0-500 nhs-u-margin--y-300 nhs-o-typography--souvenir-subhead-2-large-light">Sort</legend>
        <fieldset class="nhs-u-flex-column nhs-u-gap--300 nhs-u-padding--top-300">
          ${options.map(opt => this.getRadioOption(opt.label, opt.value)).join('')}
        </fieldset>
      </div>
    `;
  }

/**
  * Generates and returns the HTML string for the sorting drawer when displaying home search results.
  *
  * - Renders a set of radio button options for sorting homes by various criteria
  *     (price, beds, sqft, city, builder).
  * - Each option is wrapped in a labelfor accessibility and clickability.
  * - The generated HTML is used as the content for the sorting drawer when the product type is 'Homes'.
  *
  * @returns {string} The HTML string for the homes sorting drawer UI.
  */
  getHtmlForHomes() {
    const options = [
      { label: 'Default', value: '' },
      { label: 'Price (Low-High)', value: 'price,false' },
      { label: 'Price (High-Low)', value: 'price,true' },
      { label: '# Beds (Low-High)', value: 'bed,false' },
      { label: '# Beds (High-Low)', value: 'bed,true' },
      { label: 'SqFt (Low-High)', value: 'sqft,false' },
      { label: 'SqFt (High-Low)', value: 'sqft,true' },
      { label: 'City (A-Z)', value: 'city,false' },
      { label: 'Builder (A-Z)', value: 'builders,false' },
    ];
    return this.getHtmlForOptions(options);
  }

/**
  * Generates and returns the HTML string for the sorting drawer when displaying community search results.
  *
  * - Renders a set of radio button options for sorting communities by various criteria
  *     (price, review rating, sqft, city, community, builder).
  * - Each option is wrapped in a labelfor accessibility and clickability.
  * - The generated HTML is used as the content for the sorting drawer when the product type is 'Communities'.
  *
  * @returns {string} The HTML string for the communities sorting drawer UI.
  */
  getHtmlForCommunities() {
    const options = [
      { label: 'Default', value: '' },
      { label: 'Price (Low-High)', value: 'price,false' },
      { label: 'Price (High-Low)', value: 'price,true' },
      { label: 'Best Review Rating', value: 'reviewrating,true' },
      { label: 'SqFt (Low-High)', value: 'sqft,false' },
      { label: 'SqFt (High-Low)', value: 'sqft,true' },
      { label: 'City (A-Z)', value: 'city,false' },
      { label: 'Community (A-Z)', value: 'community,false' },
      { label: 'Builder (A-Z)', value: 'builders,false' },
    ];
    return this.getHtmlForOptions(options);
  }

/**
  * Returns the HTML string for the sorting drawer UI based on the current product type.
  *
  * - If the product type is 'Homes', returns the homes sorting drawer HTML.
  * - For all other product types (including 'Communities'), returns the communities sorting drawer HTML.
  *
  * @returns {string} The HTML string for the sorting drawer UI.
  */
  getHtml() {
    switch (this.productType) {
      case ProductType.Homes:
        return this.getHtmlForHomes();
      default:
        return this.getHtmlForCommunities();
    }
  }
}